import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../atoms/Button';
import { TransactionTypeSelector } from '../molecules/TransactionTypeSelector';
import { SimpleTransactionFields } from './SimpleTransactionFields';
import { SwapTransactionFields } from './SwapTransactionFields';
import { payoutSchema, type PayoutFormData } from '../../schemas/payoutSchema';
import type { Payout, PayoutType } from '../../types/payout';

interface PayoutFormRHFProps {
  onSubmit: (payout: Omit<Payout, 'id' | 'createdAt'>) => void;
  isLoading?: boolean;
  editingPayout?: Payout | null;
  onCancelEdit?: () => void;
  alwaysExpanded?: boolean;
}

// Convertit une date ISO UTC en format datetime-local local ("YYYY-MM-DDTHH:mm")
const toLocalDateTimeInput = (dateString: string): string => {
  const d = new Date(dateString);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const nowLocalDateTimeInput = () => toLocalDateTimeInput(new Date().toISOString());

/**
 * Payout form using React Hook Form with FormProvider
 * Supports simple transactions (payout, achat, vente, stacking) and swap transactions
 */
export const PayoutFormRHF: React.FC<PayoutFormRHFProps> = ({
  onSubmit,
  isLoading = false,
  editingPayout,
  onCancelEdit,
  alwaysExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const expanded = alwaysExpanded || isExpanded;

  // Initialize React Hook Form with Zod validation
  const methods = useForm<PayoutFormData>({
    resolver: zodResolver(payoutSchema),
    shouldUnregister: true,
    defaultValues: {
      date: nowLocalDateTimeInput(), // heure locale
      type: 'payout',
    },
  });

  const { handleSubmit, watch, reset, setValue, getValues, formState } = methods;
  const { errors, dirtyFields } = formState;
  const transactionType = watch('type');

  // Pour une vente en création, laisser la date vide par défaut : sinon la date « maintenant »
  // par défaut déclencherait un précalcul de la case 212 (valeur du portefeuille) sur une date
  // presque toujours fausse dès qu'une crypto est saisie. On ne touche pas à une date que
  // l'utilisateur a lui-même modifiée (dirty), ni au mode édition.
  useEffect(() => {
    if (editingPayout) return;
    if (dirtyFields.date) return;
    const currentDate = getValues('date');
    if (transactionType === 'vente' && currentDate) {
      setValue('date', '');
    } else if (transactionType !== 'vente' && !currentDate) {
      setValue('date', nowLocalDateTimeInput());
    }
  }, [transactionType, editingPayout, dirtyFields.date, getValues, setValue]);

  // Populate form when editing
  useEffect(() => {
    if (editingPayout) {
      const type = editingPayout.type || 'payout';
      const formData: PayoutFormData = {
        id: editingPayout.id,
        date: toLocalDateTimeInput(editingPayout.date),
        type,
      };

      if (type === 'swap') {
        formData.sentCrypto = editingPayout.sentCrypto || undefined;
        formData.sentAmount = editingPayout.sentAmount;
        formData.sentUnitPrice = editingPayout.sentUnitPrice;
        formData.receivedCrypto = editingPayout.receivedCrypto || undefined;
        formData.receivedAmount = editingPayout.receivedAmount;
        formData.receivedUnitPrice = editingPayout.receivedUnitPrice;
        formData.fees = editingPayout.fees;
        formData.platform = editingPayout.platform;
      } else {
        formData.crypto = editingPayout.crypto || undefined;
        formData.amount = editingPayout.amount;
        formData.unitPrice = editingPayout.unitPrice;
        if (type === 'achat') {
          formData.purchaseAmount = editingPayout.totalValue;
        }
        if (type === 'vente') {
          formData.portfolioValueAtSale = editingPayout.portfolioValueAtSale;
        }
      }

      // Reset the whole form then explicitly force the type so watch('type')
      // is guaranteed to reflect the editing transaction (shouldUnregister: true
      // can cause reset() alone to not propagate the type when fields are unmounted)
      reset(formData);
      setValue('type', type);
      if (!alwaysExpanded) setIsExpanded(true);
    }
  }, [editingPayout, reset, setValue, alwaysExpanded]);

  // Submit handler
  const onFormSubmit = (data: PayoutFormData) => {
    let payout: Omit<Payout, 'id' | 'createdAt'>;

    if (data.type === 'swap') {
      // Build swap transaction
      const sentTotal = (data.sentAmount || 0) * (data.sentUnitPrice || 0);
      const receivedTotal = (data.receivedAmount || 0) * (data.receivedUnitPrice || 0);

      payout = {
        date: new Date(data.date).toISOString(), // datetime-local local → UTC ISO
        type: 'swap',
        sentCrypto: data.sentCrypto!,
        sentAmount: data.sentAmount!,
        sentUnitPrice: data.sentUnitPrice!,
        receivedCrypto: data.receivedCrypto!,
        receivedAmount: data.receivedAmount!,
        receivedUnitPrice: data.receivedUnitPrice!,
        fees: data.fees,
        platform: data.platform,
        sentTotalValue: sentTotal,
        receivedTotalValue: receivedTotal,
      };
    } else {
      // Build simple transaction
      const totalValue = data.type === 'achat'
        ? data.purchaseAmount || 0
        : (data.amount || 0) * (data.unitPrice || 0);

      payout = {
        date: new Date(data.date).toISOString(), // datetime-local local → UTC ISO
        type: data.type as Exclude<PayoutType, 'swap'>,
        crypto: data.crypto!,
        amount: data.amount!,
        unitPrice: data.type === 'achat' ? undefined : data.unitPrice,
        totalValue,
        portfolioValueAtSale: data.type === 'vente' ? data.portfolioValueAtSale : undefined,
      };
    }

    onSubmit(payout);
    reset();
    if (!alwaysExpanded) setIsExpanded(false);
  };

  const handleCancel = () => {
    reset();
    if (!alwaysExpanded) setIsExpanded(false);
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  const getFormTitle = () => {
    if (editingPayout) return 'Modifier la transaction';
    switch (transactionType) {
      case 'payout':
        return 'Déclarer un Payout';
      case 'stacking':
        return 'Enregistrer un Stacking';
      case 'achat':
        return 'Enregistrer un Achat';
      case 'vente':
        return 'Enregistrer une Vente';
      case 'swap':
        return 'Enregistrer un Swap';
      default:
        return 'Enregistrer une Transaction';
    }
  };

  const getFormDescription = () => {
    if (editingPayout) return 'Modifiez les informations de cette transaction';
    switch (transactionType) {
      case 'payout':
        return 'Enregistrez vos revenus de minage pour la déclaration BNC';
      case 'stacking':
        return 'Enregistrez vos revenus de stacking pour la déclaration BNC';
      case 'achat':
        return 'Enregistrez un achat de tokens pour votre portefeuille';
      case 'vente':
        return 'Enregistrez une vente pour calculer les plus-values';
      case 'swap':
        return 'Enregistrez un échange crypto-to-crypto';
      default:
        return '';
    }
  };

  const getSubmitButtonLabel = () => {
    if (isLoading) {
      return editingPayout ? 'Modification...' : 'Enregistrement...';
    }
    if (editingPayout) return 'Sauvegarder les modifications';
    switch (transactionType) {
      case 'payout':
        return 'Enregistrer le Payout';
      case 'stacking':
        return 'Enregistrer le Stacking';
      case 'achat':
        return "Enregistrer l'Achat";
      case 'vente':
        return 'Enregistrer la Vente';
      case 'swap':
        return 'Enregistrer le Swap';
      default:
        return 'Enregistrer';
    }
  };

  if (alwaysExpanded) {
    return (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <TransactionTypeSelector
            selectedType={transactionType}
            onTypeChange={(type: PayoutType) => setValue('type', type)}
            isEditing={!!editingPayout}
          />

          {transactionType === 'swap' ? (
            <SwapTransactionFields />
          ) : (
            <SimpleTransactionFields
              key={editingPayout?.id ?? 'new'}
              transactionType={transactionType as Exclude<PayoutType, 'swap'>}
              initialUnitPrice={editingPayout?.unitPrice}
              initialPortfolioValueAtSale={editingPayout?.portfolioValueAtSale}
              initialDate={editingPayout ? toLocalDateTimeInput(editingPayout.date) : undefined}
              initialCryptoId={editingPayout?.crypto?.id}
            />
          )}

          <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1"
            >
              {getSubmitButtonLabel()}
            </Button>
            {(editingPayout || transactionType !== 'payout') && (
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Annuler
              </Button>
            )}
          </div>

          {errors.type && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
              {errors.type.message}
            </div>
          )}
        </form>
      </FormProvider>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-4">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between py-2 text-left focus:outline-none focus:ring-2 focus:ring-white/30 focus:ring-inset rounded-lg hover:bg-white/10 transition-colors"
            type="button"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{getFormTitle()}</h2>
                <p className="text-blue-100 text-sm mt-1">{getFormDescription()}</p>
              </div>
            </div>
            <svg
              className={`w-6 h-6 text-white transform transition-transform duration-200 ${
                expanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Form Content */}
        {expanded && (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
              <TransactionTypeSelector
                selectedType={transactionType}
                onTypeChange={(type: PayoutType) => setValue('type', type)}
                isEditing={!!editingPayout}
              />

              {transactionType === 'swap' ? (
                <SwapTransactionFields />
              ) : (
                <SimpleTransactionFields
                  key={editingPayout?.id ?? 'new'}
                  transactionType={transactionType as Exclude<PayoutType, 'swap'>}
                  initialUnitPrice={editingPayout?.unitPrice}
                  initialPortfolioValueAtSale={editingPayout?.portfolioValueAtSale}
                  initialDate={editingPayout ? toLocalDateTimeInput(editingPayout.date) : undefined}
                  initialCryptoId={editingPayout?.crypto?.id}
                />
              )}

              <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1"
                >
                  {getSubmitButtonLabel()}
                </Button>
                {(editingPayout || transactionType !== 'payout') && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Annuler
                  </Button>
                )}
              </div>

              {errors.type && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded">
                  {errors.type.message}
                </div>
              )}
            </form>
          </FormProvider>
        )}
      </div>
    </div>
  );
};
